import type React from "react";
import { useState, useEffect, useMemo } from "react"
import { BiBell, BiSearch, BiSend, BiX, BiMessageSquareDetail } from "react-icons/bi"
import { CiSettings } from "react-icons/ci";
import { FaBullhorn, FaEye, FaUsers } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  createBroadcast,
  getAllBroadcasts,
  getBroadcastById,
  resetCreateStatus,
  clearErrors,
} from "../../../redux/restaurantCommunication/restaurantCommunicationSlice";
import { getAllRestaurants } from "../../../redux/restaurants/restaurantsSlice";
import toast from "react-hot-toast";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode;
  variant?: "default" | "ghost" | "destructive" | "secondary";
  size?: "default" | "sm";
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Input Component
const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

// Custom Card Components
const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardHeader = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
      {children}
    </div>
  );
};

const CardTitle = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

const CardContent = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export default function RestaurantCommunicationCenter() {
  const dispatch = useAppDispatch();
  const {
    broadcasts,
    status,
    createStatus,
    currentBroadcast,
    detailsStatus,
    error,
  } = useAppSelector((state: RootState) => state.restaurantCommunication);
  const { restaurants } = useAppSelector(
    (state: RootState) => state.restaurants
  );

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [messageType, setMessageType] = useState<string>("all");
  const [issueType, setIssueType] = useState<string>("system");
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([])
  const [showRestaurantSelector, setShowRestaurantSelector] = useState(false)
  const [recipientSearch, setRecipientSearch] = useState<string>("")
  const [locationFilter, setLocationFilter] = useState<string>("")

  useEffect(() => {
    dispatch(getAllBroadcasts())
    dispatch(getAllRestaurants())
  }, [])

  // Refetch restaurants when filtering by location (debounced)
  useEffect(() => {
    if (messageType !== "individual") return;
    const handler = setTimeout(() => {
      const q = locationFilter.trim();
      if (q) {
        dispatch(getAllRestaurants({ location: q }))
      } else {
        dispatch(getAllRestaurants())
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [locationFilter, messageType, dispatch])

  useEffect(() => {}, [restaurants]);

  useEffect(() => {
    if (createStatus === "success") {
      toast.success("Broadcast created successfully!");
      dispatch(resetCreateStatus());
      // Reset form
      setTitle("");
      setDescription("");
      setSelectedRestaurants([]);
      setMessageType("all");
      setIssueType("system");
    } else if (createStatus === "failed" && error) {
      toast.error(error);
      dispatch(clearErrors());
    }
  }, [createStatus, error, dispatch]);

  const handleMessageDetails = (message: any) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
  };

  const handleSendMessage = () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a message");
      return;
    }
    if (messageType === "individual" && selectedRestaurants.length === 0) {
      toast.error("Please select at least one restaurant");
      return;
    }

    const payload = {
      issueType,
      title,
      description,
      isAll: messageType === "all",
      ...(messageType === "individual" && { recipients: selectedRestaurants }),
    };

    dispatch(createBroadcast(payload));
  };

  const handleRestaurantSelection = (restaurantId: string) => {
    setSelectedRestaurants((prev) =>
      prev.includes(restaurantId)
        ? prev.filter((id) => id !== restaurantId)
        : [...prev, restaurantId]
    )
  }

  const filteredRestaurantsList = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase()
    const loc = locationFilter.trim().toLowerCase()
    const list: any[] = restaurants || []
    const filteredBySearch = !q
      ? list
      : list.filter((r: any) =>
          (r.name || "").toLowerCase().includes(q) ||
          String(r.restaurantGeneratedID || r._id || r.id || "")
            .toLowerCase()
            .includes(q)
        )
    const filteredByLocation = !loc
      ? filteredBySearch
      : filteredBySearch.filter((r: any) =>
          (r.location || r.address || r.state || "")
            .toLowerCase()
            .includes(loc)
        )
    return filteredByLocation
  }, [restaurants, recipientSearch, locationFilter])

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="mb-4 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center justify-between">
          <div>
            <div className="relative flex items-center">
              <span className="text-lg font-semibold">Restaurants</span>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-lg font-semibold text-green-600">
                Communication Center
              </span>
            </div>
            <p className="-mt-2 text-sm text-gray-500">
              Send messages, announcements, and updates to restaurant partners.
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Message */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Compose Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Message Type
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                >
                  <option value="all">Broadcast to All Restaurants</option>
                  <option value="individual">Individual Restaurants</option>
                </select>
              </div>

              {messageType === "individual" && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Select Restaurants ({selectedRestaurants.length} selected)
                  </label>

                  <div className="relative mb-2">
                    <Input
                      className="w-full pl-9"
                      placeholder="Search restaurants by name or ID..."
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                    />
                    <BiSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>

                  <div className="relative mb-2">
                    <Input
                      className="w-full pl-9"
                      placeholder="Filter by location (e.g., Cross River State)"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                    />
                    <BiSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>

                  {restaurants.length === 0 ? (
                    <div className="border border-gray-300 rounded-md p-4 text-center text-gray-500">
                      Loading restaurants...
                    </div>
                  ) : filteredRestaurantsList.length === 0 ? (
                    <div className="border border-gray-300 rounded-md p-4 text-center text-gray-500">
                      No restaurants match "{recipientSearch}"
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {filteredRestaurantsList.map((restaurant: any) => {
                        const restaurantId = restaurant._id || restaurant.id || restaurant.restaurantGeneratedID
                        return (
                          <label
                            key={restaurantId}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedRestaurants.includes(restaurantId)}
                              onChange={() => handleRestaurantSelection(restaurantId)}
                              className="rounded"
                            />
                            <span className="text-sm">{restaurant.name || restaurant.restaurantGeneratedID}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Issue Type
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                >
                  <option value="system">System</option>
                  <option value="payment">Payment</option>
                  <option value="order">Order</option>
                  <option value="policy">Policy</option>
                  <option value="promotion">Promotion</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Subject
                </label>
                <Input
                  className="w-full"
                  placeholder="Enter Message Subject..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Message
                </label>
                <textarea
                  className="focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full p-3 border border-gray-300 rounded-md h-32 resize-none"
                  placeholder="Enter Your message here"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Button
                className="bg-green-600 text-white hover:bg-green-700"
                onClick={handleSendMessage}
                disabled={createStatus === "loading"}
              >
                {createStatus === "loading" ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Sending...
                  </>
                ) : (
                  <>
                    <BiSend className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & Stats */}
        <div className="h-full space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col space-y-3">
              <Button
                className="flex w-full items-center justify-start text-gray-700 bg-white hover:bg-gray-50 p-2 rounded border"
                variant="ghost"
                onClick={() => {
                  setMessageType("all");
                  setIssueType("system");
                  setTitle("Menu Update Reminder");
                  setDescription("Please ensure all menu items are updated");
                }}
              >
                <BiBell className="w-4 h-4 mr-3" />
                <span>Menu Update Reminder</span>
              </Button>
              <Button
                className="flex w-full items-center justify-start text-gray-700 bg-white hover:bg-gray-50 p-2 rounded border"
                variant="ghost"
                onClick={() => {
                  setMessageType("all");
                  setIssueType("promotion");
                  setTitle("New Promotion Announcement");
                  setDescription(
                    "Exciting new promotions available for your restaurant"
                  );
                }}
              >
                <FaBullhorn className="w-4 h-4 mr-3" />
                <span>Announce New Promotions</span>
              </Button>
              <Button
                className="flex w-full items-center justify-start text-gray-700 bg-white hover:bg-gray-50 p-2 rounded border"
                variant="ghost"
                onClick={() => {
                  setMessageType("all");
                  setIssueType("other");
                  setTitle("Top Performers Recognition");
                  setDescription(
                    "Congratulations to our top performing restaurants"
                  );
                }}
              >
                <FaUsers className="w-4 h-4 mr-3" />
                <span>Notify Top Performers</span>
              </Button>
              <Button
                className="flex w-full items-center justify-start text-gray-700 bg-white hover:bg-gray-50 p-2 rounded border"
                variant="ghost"
                onClick={() => {
                  setMessageType("all");
                  setIssueType("policy");
                  setTitle("Policy Update");
                  setDescription(
                    "Important policy updates for all restaurants"
                  );
                }}
              >
                <BiMessageSquareDetail className="w-4 h-4 mr-3" />
                <span>Send Policy Update</span>
              </Button>

              <div className="pt-7">
                <p className="text-lg font-semibold mb-3">
                  Communication Stats
                </p>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Messages sent:</span>
                  <span className="font-medium">{broadcasts.length}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    Total Restaurants:
                  </span>
                  <span className="font-medium text-green-600">
                    {restaurants.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Broadcasts:</span>
                  <span className="font-medium">
                    {broadcasts.filter((b) => b.isAll).length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Message History */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Message History</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === "loading" && (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            )}
            {status === "success" && broadcasts.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No broadcasts found. Create your first broadcast above.
              </div>
            )}
            {status === "success" &&
              broadcasts.length > 0 &&
              broadcasts.map((broadcast) => {
                const getIconByType = (type: string) => {
                  switch (type) {
                    case "payment":
                      return { icon: FaBullhorn, color: "yellow" };
                    case "order":
                      return { icon: FaUsers, color: "green" };
                    case "policy":
                      return { icon: BiMessageSquareDetail, color: "blue" };
                    case "promotion":
                      return { icon: FaBullhorn, color: "purple" };
                    default:
                      return { icon: CiSettings, color: "gray" };
                  }
                };

                const { icon: Icon, color } = getIconByType(
                  broadcast.issueType
                );
                const recipientCount = Array.isArray(broadcast.recipients)
                  ? broadcast.recipients.length
                  : 0;
                const formattedDate = new Date(
                  broadcast.createdAt
                ).toLocaleDateString("en-US", {
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
                });

                return (
                  <div
                    key={broadcast._id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 bg-${color}-100 rounded-full flex items-center justify-center`}
                      >
                        <Icon className={`w-4 h-4 text-${color}-600`} />
                      </div>
                      <div>
                        <p className="font-medium">
                          {broadcast.title} .{" "}
                          {broadcast.isAll ? "Broadcast" : "Targeted"}
                        </p>
                        <p className="text-sm text-green-600">
                          To:{" "}
                          {broadcast.isAll
                            ? "All Restaurants"
                            : `Selected (${recipientCount})`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formattedDate} | {broadcast.issueType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className="bg-green-100 text-green-800">
                        Sent
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#0000008C] border border-[#000000BF]"
                        onClick={() => {
                          dispatch(getBroadcastById(broadcast._id));
                          handleMessageDetails(broadcast);
                        }}
                      >
                        View
                        <FaEye className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      </div>

      {/* Message Details Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Message Details
                </h3>
                <p className="text-sm text-gray-500">
                  View detailed information about the broadcast "
                  {selectedMessage.title}"
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMessageModal(false)}
              >
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-medium text-yellow-600">
                  {selectedMessage.title}
                </h4>
                <Badge className="bg-yellow-100 text-yellow-800">
                  {selectedMessage.isAll ? "Broadcast" : "Targeted"}
                </Badge>
              </div>

              <div>
                <h5 className="font-medium mb-2">Message Content</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {selectedMessage.description}
                  </p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Delivery Details</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Issue Type:</p>
                    <p className="text-sm text-gray-500 capitalize">
                      {selectedMessage.issueType}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Recipients Count:</p>
                    <p className="text-sm text-gray-500">
                      {Array.isArray(selectedMessage.recipients)
                        ? selectedMessage.recipients.length
                        : 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Sent at:</p>
                    <p className="text-sm text-gray-500">
                      {new Date(selectedMessage.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Broadcast Type:</p>
                    <p className="text-sm text-gray-500">
                      {selectedMessage.isAll
                        ? "All Restaurants"
                        : "Selected Restaurants"}
                    </p>
                  </div>
                </div>
              </div>

              {currentBroadcast &&
                Array.isArray(currentBroadcast.recipients) &&
                currentBroadcast.recipients.length > 0 &&
                typeof currentBroadcast.recipients[0] === "object" && (
                  <div>
                    <h5 className="font-medium mb-3">Recipients</h5>
                    <div className="max-h-40 overflow-y-auto bg-gray-50 p-4 rounded-lg">
                      <ul className="space-y-2">
                        {(currentBroadcast.recipients as any[]).map(
                          (recipient: any, index: number) => (
                            <li
                              key={recipient._id || index}
                              className="text-sm text-gray-700"
                            >
                              {recipient.businessInformation?.businessName ||
                                recipient._id}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
